/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */
import { withFormik, FormikBag } from "formik";

import { IUserProfile } from "../../Interfaces/UserProfile/IUserProfile";
import { convertFromDateTimeToDate } from "../../Utility/FormatHelper";
import { IStudentEditProfileFormValues } from "./Interfaces/IStudentEditProfileFormValues";
import { IStudentEditProfileProps } from "./Interfaces/IStudentEditProfileProps";
import StudentEditProfileForm from "./StudentEditProfileForm";
import StudentProfileValidationSchema from "./StudentProfileValidationSchema";

export const StudentEditProfileEnhancedForm = withFormik<
  IStudentEditProfileProps,
  IStudentEditProfileFormValues
>({
  // TODO: enable validation
  validationSchema: StudentProfileValidationSchema,
  validateOnMount: true,
  // Set these fields to touched state on initial load to get the validate results
  mapPropsToTouched: (props) => {
    const shouldValidate = props.canEditUser;
    return {
      titleName: shouldValidate,
      firstName: shouldValidate,
      lastName: shouldValidate,
      localizedFirstName: shouldValidate,
      localizedLastName: shouldValidate,
      genderName: shouldValidate,
      genderPronounPreferenceName: shouldValidate,
      shirtFitType: shouldValidate,
      shirtSize: shouldValidate,
      languages: shouldValidate,
      dateOfBirth: shouldValidate,
      biography: shouldValidate,
      headline: shouldValidate,
      address1: shouldValidate,
      address2: shouldValidate,
      addressCity: shouldValidate,
      addressPostCode: shouldValidate,
      addressCountryOrRegionName: shouldValidate,
      primaryPhoneCountryOrRegionTapiCode: shouldValidate,
      primaryPhone: shouldValidate,
      shippingAddress1: shouldValidate,
      shippingAddress2: shouldValidate,
      shippingCity: shouldValidate,
      shippingPostCode: shouldValidate,
      shippingCountryOrRegionName: shouldValidate,
      shippingPhoneCountryOrRegionName: shouldValidate,
      shippingPhoneNumber: shouldValidate,
      userProfileSchool: [
        {
          schoolCountryRegionName: shouldValidate,
          schoolName: shouldValidate,
          schoolDegreeLevelName: shouldValidate,
          schoolDegreeProgramName: shouldValidate,
          expectedGraduationDate: shouldValidate,
        },
      ],
      userProfileSocialNetwork: [
        {
          socialNetworkName: shouldValidate,
          handle: shouldValidate,
        },
      ],
    };
  },
  mapPropsToValues: (props) => ({
    address1: props.selectedUserProfile.address1 || "",
    address2: props.selectedUserProfile.address2 || "",
    addressCity: props.selectedUserProfile.addressCity || "",
    addressCountryOrRegionName: props.selectedUserProfile.addressCountryOrRegionName || "",
    addresses: props.selectedUserProfile.addresses,
    addressPostCode: props.selectedUserProfile.addressPostCode || "",
    addressStateOrProvince: props.selectedUserProfile.addressStateOrProvince,
    awardNameLocalized: props.selectedUserProfile.awardNameLocalized,
    biography: props.selectedUserProfile.biography,
    companyName: props.selectedUserProfile.companyName || "",
    companySize: props.selectedUserProfile.companySize || "",
    companyUrl: props.selectedUserProfile.companyUrl || "",
    microsoftReferralId: props.selectedUserProfile.microsoftReferralId || "",
    dateOfBirth:
      props.selectedUserProfile.dateOfBirth != undefined
        ? convertFromDateTimeToDate(props.selectedUserProfile.dateOfBirth)
        : undefined,
    firstName: props.selectedUserProfile.firstName,
    genderName: props.selectedUserProfile.genderName || "",
    genderPronounPreferenceName: props.selectedUserProfile.genderPronounPreferenceName,
    headline: props.selectedUserProfile.headline,
    id: props.selectedUserProfile.id,
    isPrivacyAgreed: props.selectedUserProfile.isPrivacyAgreed,
    isShippingBlocked: props.selectedUserProfile.isShippingBlocked,
    isTextOptIn: props.selectedUserProfile.isTextOptIn,
    languages: props.selectedUserProfile.languages,
    lastName: props.selectedUserProfile.lastName,
    localizedFirstName: props.selectedUserProfile.localizedFirstName,
    localizedLastName: props.selectedUserProfile.localizedLastName,
    onboardingWizardStep: props.selectedUserProfile.onboardingWizardStep,
    phoneContacts: props.selectedUserProfile.phoneContacts,
    primaryAddressId: props.selectedUserProfile.primaryAddressId,
    primaryEmail: props.selectedUserProfile.primaryEmail,
    primaryPhoneCountryOrRegionTapiCode: props.countryRegionPhoneOptionList.find(
      (x) => x.id === props.selectedUserProfile.primaryPhoneCountryOrRegionId
    )?.tapi,
    primaryPhoneCountryOrRegionName: props.countryRegionPhoneOptionList.find(
      (x) => x.id === props.selectedUserProfile.primaryPhoneCountryOrRegionId
    )?.key,
    primaryPhone: props.selectedUserProfile.primaryPhone,
    primaryPhoneCountryOrRegionId: props.selectedUserProfile.primaryPhoneCountryOrRegionId,
    primaryPhoneId: props.selectedUserProfile.primaryPhoneId,
    profileImageUrl: props.selectedUserProfile.profilePictureUrl,
    profilePictureBase64: props.selectedUserProfile.profilePictureBase64,
    programProfileBenefit: props.selectedUserProfile.programProfileBenefit,
    programProfileStatusId: props.selectedUserProfile.programProfileStatusId,
    programProfileStatusName: props.selectedUserProfile.programProfileStatusName,
    screenNameLocalized: props.selectedUserProfile.screenNameLocalized,
    secondaryEmail: props.selectedUserProfile.secondaryEmail || "",
    shippingAddress1: props.selectedUserProfile.shippingAddress1 || "",
    shippingAddress2: props.selectedUserProfile.shippingAddress2 || "",
    shippingAddressId: props.selectedUserProfile.shippingAddressId,
    shippingCity: props.selectedUserProfile.shippingCity || "",
    shippingCountryOrRegionName: props.selectedUserProfile.shippingCountryOrRegionName || "",
    shippingName: props.selectedUserProfile.shippingName || "",
    shippingPhoneCountryOrRegionId: props.selectedUserProfile.shippingPhoneCountryOrRegionId,
    shippingPhoneId: props.selectedUserProfile.shippingPhoneId,
    shippingPhoneCountryOrRegionTapiCode: props.countryRegionPhoneOptionList.find(
      (x) => x.id === props.selectedUserProfile.shippingPhoneCountryOrRegionId
    )?.tapi,
    shippingPhoneCountryOrRegionName: props.countryRegionPhoneOptionList.find(
      (x) => x.id === props.selectedUserProfile.shippingPhoneCountryOrRegionId
    )?.key,
    shippingPhoneNumber: props.selectedUserProfile.shippingPhoneNumber || "",
    shippingPostCode: props.selectedUserProfile.shippingPostCode || "",
    shippingSameAsAddress: props.selectedUserProfile.shippingSameAsAddress,
    shippingStateOrProvince: props.selectedUserProfile.shippingStateOrProvince || "",
    shirtFitType: props.selectedUserProfile.shirtFitType || "",
    shirtSize: props.selectedUserProfile.shirtSize || "",
    studentPartnerEmail: props.selectedUserProfile.studentPartnerEmail || "",
    tenants: props.selectedUserProfile.tenants,
    titleName: props.selectedUserProfile.titleName || "Choose not to answer",
    userProfileSchool: props.selectedUserProfile.userProfileSchool.map((school) => {
      return {
        ...school,
        schoolCountryRegionName: school.schoolCountryRegionName || "",
        schoolDegreeProgramName: school.schoolDegreeProgramName || "",
        schoolDegreeLevelName: school.schoolDegreeLevelName || "",
        expectedGraduationDate:
          school.expectedGraduationDate != undefined
            ? convertFromDateTimeToDate(school.expectedGraduationDate)
            : undefined,
      };
    }),
    userProfileSocialNetwork: props.selectedUserProfile.userProfileSocialNetwork,
    userProfileProgramLanguage: props.selectedUserProfile.userProfileProgramLanguage,
    countryRegionOptionList: props.countryRegionOptionList,
    countryRegionPhoneOptionList: props.countryRegionPhoneOptionList,
    stateProvinceOptionList: props.stateProvinceOptionList,
    stateProvinceShippingOptionList: props.stateProvinceShippingOptionList,
    stateProvinceSchoolOptionList: props.stateProvinceSchoolOptionList,
    schoolOptionList: props.schoolOptionList,
    schoolOptionsSelected: props.schoolOptionsSelected,
    socialNetworkOptionList: props.socialNetworkOptionList,
    programLanguageOptionList: props.programLanguageOptionList,
  }),
  handleSubmit: (
    values: IStudentEditProfileFormValues,
    { props, setSubmitting }: FormikBag<IStudentEditProfileProps, IStudentEditProfileFormValues>
  ) => {
    setSubmitting(false);

    const userProfile: IUserProfile = {
      address1: values.address1,
      address2: values.address2,
      addressCity: values.addressCity,
      addressCountryOrRegionName: values.addressCountryOrRegionName,
      addresses: values.addresses,
      addressPostCode: values.addressPostCode,
      addressStateOrProvince: values.addressStateOrProvince,
      application: props.selectedUserProfile.application,
      awardNameLocalized: values.awardNameLocalized,
      biography: values.biography,
      companyName: values.companyName,
      companyRole: props.selectedUserProfile.companyRole,
      companySize: values.companySize,
      companyUrl: values.companyUrl,
      microsoftReferralId: values.microsoftReferralId,
      createdBy: props.selectedUserProfile.createdBy,
      dateCreated: props.selectedUserProfile.dateCreated,
      dateDeleted: props.selectedUserProfile.dateDeleted,
      dateModified: new Date(),
      dateOfBirth: values.dateOfBirth,
      deleted: props.selectedUserProfile.deleted,
      deletedBy: props.selectedUserProfile.deletedBy,
      firstName: values.firstName,
      fullPhoneNumber: props.selectedUserProfile.fullPhoneNumber,
      genderName: values.genderName,
      genderPronounPreferenceName: values.genderPronounPreferenceName,
      headline: values.headline,
      id: props.selectedUserProfile.id,
      isOnboardingWizardComplete: props.selectedUserProfile.isOnboardingWizardComplete,
      isPrivacyAgreed: values.isPrivacyAgreed,
      isShippingBlocked: values.isShippingBlocked,
      isTextOptIn: values.isTextOptIn,
      languages: values.languages,
      lastName: values.lastName,
      levelStatus: props.selectedUserProfile.levelStatus,
      localizedFirstName: values.localizedFirstName,
      localizedLastName: values.localizedLastName,
      modifiedBy: props.selectedUserProfile.modifiedBy,
      newSchoolAdded: props.selectedUserProfile.newSchoolAdded,
      onboardingWizardStep: values.onboardingWizardStep,
      onboardingWizardStepId: props.selectedUserProfile.onboardingWizardStepId,
      phoneContacts: values.phoneContacts,
      pictureLink: props.selectedUserProfile.pictureLink,
      primaryAddressId: props.selectedUserProfile.primaryAddressId,
      primaryEmail: values.primaryEmail,
      primaryPhone: values.primaryPhone,
      primaryPhoneCountryOrRegionId: values.primaryPhoneCountryOrRegionId,
      primaryPhoneId: props.selectedUserProfile.primaryPhoneId,
      profilePictureBase64: values.profilePictureBase64,
      profilePictureUrl: values.profileImageUrl,
      programProfileBenefit: props.selectedUserProfile.programProfileBenefit,
      programProfileStatusId: props.selectedUserProfile.programProfileStatusId,
      programProfileStatusName: props.selectedUserProfile.programProfileStatusName,
      programProfileStartDate: props.selectedUserProfile.programProfileStartDate,
      programProfileEndDate: props.selectedUserProfile.programProfileEndDate,
      puid: props.selectedUserProfile.puid,
      renewalStatusName: props.selectedUserProfile.renewalStatusName,
      screenNameLocalized: values.screenNameLocalized,
      secondaryEmail: values.secondaryEmail,
      shippingAddress1: values.shippingAddress1,
      shippingAddress2: values.shippingAddress2,
      shippingAddressId: props.selectedUserProfile.shippingAddressId,
      shippingCity: values.shippingCity,
      shippingCompany: props.selectedUserProfile.shippingCompany,
      shippingCountryOrRegionId: props.selectedUserProfile.shippingCountryOrRegionId,
      shippingCountryOrRegionName: values.shippingCountryOrRegionName,
      shippingName: values.shippingName,
      shippingPhoneCountryOrRegionId: values.shippingPhoneCountryOrRegionId,
      shippingPhoneId: props.selectedUserProfile.shippingPhoneId,
      shippingPhoneNumber: values.shippingPhoneNumber,
      shippingPostCode: values.shippingPostCode,
      shippingSameAsAddress: values.shippingSameAsAddress,
      shippingStateOrProvince: values.shippingStateOrProvince,
      shirtFitType: values.shirtFitType,
      shirtSize: values.shirtSize,
      studentPartnerEmail: values.studentPartnerEmail,
      tenants: values.tenants,
      titleName: values.titleName,
      userProfileIdentifier: props.selectedUserProfile.userProfileIdentifier,
      userProfileSchool: values.userProfileSchool,
      userProfileSocialNetwork: values.userProfileSocialNetwork,
      userProfileProgramLanguage: values.userProfileProgramLanguage,
    };

    props.updateUserProfile(userProfile);
  },
  displayName: "Student Profile Form",
})(StudentEditProfileForm);

export default StudentEditProfileEnhancedForm;
